import {
  AfterViewInit,
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Subject, delay, filter } from 'rxjs';

export interface IntersectionObserverOption {
  rootMargin: string;
  threshold: number;
}

@Directive({
  selector: '[inViewport]',
})
export class InViewportDirective implements OnDestroy, OnInit, AfterViewInit {
  @Input() debounceTime: number = 0;
  @Input() threshold = 0;
  @Input() lastElement = false;
  @Input() rootMargin = '20px 0px 0px 0px';
  @Output() elementIsVisible = new EventEmitter<HTMLElement>();
  private isVisibleSubject$: Subject<{
    target: HTMLElement;
    observer: IntersectionObserver;
  }> = new Subject<{
    target: HTMLElement;
    observer: IntersectionObserver;
  }>();
  private elementObserver: IntersectionObserver | undefined;
  private elementSubject$ = new Subject<{
    entry: IntersectionObserverEntry;
    observer: IntersectionObserver;
  }>();

  constructor(private element: ElementRef) {}

  ngOnInit() {
    const options = {
      rootMargin: this.rootMargin,
      threshold: this.threshold,
    };
    this.createObserver(options);
  }

  ngAfterViewInit() {
    this.startObservingElements();
    this.subscribeIsVisibleSubject();
  }

  ngOnDestroy() {
    if (this.elementObserver) {
      this.elementObserver.disconnect();
      this.elementObserver = undefined;
    }
    this.elementSubject$.complete();
  }

  private changeIsVisibleSubjectIfVisible(
    element: HTMLElement,
    intersectionObserver: IntersectionObserver
  ) {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.intersectionRatio > 0) {
        this.isVisibleSubject$.next({
          target: element,
          observer: intersectionObserver,
        });
      }
      observer.disconnect();
    });
    observer.observe(element);
  }

  private subscribeIsVisibleSubject() {
    const visibleSubscription = this.isVisibleSubject$.subscribe(
      ({ target, observer }) => {
        this.emitIntersectionEvent(target, observer);
        visibleSubscription.unsubscribe();
      }
    );
  }

  private createObserver(options: IntersectionObserverOption) {
    this.elementObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (this.isIntersecting(entry)) {
          this.elementSubject$.next({ entry, observer });
        }
      });
    }, options);
  }

  // eslint-disable-next-line class-methods-use-this
  private isIntersecting(entry: IntersectionObserverEntry) {
    return entry.isIntersecting || entry.intersectionRatio > 0;
  }

  private startObservingElements() {
    if (!this.elementObserver) {
      return;
    }
    this.elementObserver.observe(this.element.nativeElement);
    this.elementSubject$
      .pipe(delay(this.debounceTime), filter(Boolean))
      .subscribe(({ entry, observer }) => {
        const target = entry.target as HTMLElement;
        this.changeIsVisibleSubjectIfVisible(target, observer);
      });
  }

  private emitIntersectionEvent(
    target: HTMLElement,
    observer: IntersectionObserver
  ) {
    observer.unobserve(target);
    if (this.lastElement) {
      this.elementIsVisible.emit(target);
    }
  }
}
